import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addWeeks, subWeeks, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { 
  Fish, CalendarDots, ChartLine, FishSimple, Images, SignOut, House, List, X,
  CaretLeft, CaretRight, Users, CurrencyEur, Prohibit, Printer, Download,
  Eye, Pencil, Trash, CalendarBlank, Rows, SquaresFour, Clock,
  EnvelopeSimple, PlusCircle, UserPlus, Receipt
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day');
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  
  // Dialogs
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [manualBookingDialogOpen, setManualBookingDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newSlot, setNewSlot] = useState('');
  
  // Block form
  const [blockForm, setBlockForm] = useState({ date: '', block_type: 'full_day', trip_id: '', reason: '' });
  
  // Notify
  const [notifyBookingIds, setNotifyBookingIds] = useState([]);
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Manual Booking
  const [manualForm, setManualForm] = useState({
    trip_id: '', booking_date: '', time_slot: '', selected_duration: null,
    is_privatization: false, customer_email: '', customer_phone: '', notes: '',
    payment_status: 'on_site', send_to_client: false, generate_payment_link: false,
    participants: [{ first_name: '', last_name: '', age: '', phone: '' }]
  });

  const printRef = useRef();

  const getDateRange = useCallback(() => {
    if (viewMode === 'day') {
      const d = format(currentDate, 'yyyy-MM-dd');
      return { start: d, end: d, date: d };
    } else if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') };
    } else {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') };
    }
  }, [currentDate, viewMode]);

  const fetchBookings = useCallback(async () => {
    try {
      const range = getDateRange();
      let url = `${API}/admin/bookings?`;
      if (range.date) url += `date=${range.date}`;
      else url += `start_date=${range.start}&end_date=${range.end}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(response.data);
    } catch (error) {
      if (error.response?.status === 401) { logout(); navigate('/admin'); }
    }
  }, [token, logout, navigate, getDateRange]);

  const fetchTrips = useCallback(async () => {
    try { const r = await axios.get(`${API}/trips`); setTrips(r.data); } catch (e) {}
  }, []);

  const fetchBlockedDates = useCallback(async () => {
    try {
      const month = format(currentDate, 'yyyy-MM');
      const r = await axios.get(`${API}/admin/blocked-dates?month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      setBlockedDates(r.data);
    } catch (e) {}
  }, [token, currentDate]);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } }); setStats(r.data); } catch (e) {}
  }, [token]);

  useEffect(() => {
    const load = async () => { setLoading(true); await Promise.all([fetchBookings(), fetchStats(), fetchTrips(), fetchBlockedDates()]); setLoading(false); };
    load();
  }, [fetchBookings, fetchStats, fetchTrips, fetchBlockedDates]);

  const navigatePrev = () => {
    if (viewMode === 'day') setCurrentDate(addDays(currentDate, -1));
    else if (viewMode === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subMonths(currentDate, 1));
  };
  const navigateNext = () => {
    if (viewMode === 'day') setCurrentDate(addDays(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addMonths(currentDate, 1));
  };
  const goToday = () => setCurrentDate(new Date());

  // Cancel
  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    try {
      await axios.delete(`${API}/admin/bookings/${selectedBooking.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Réservation annulée');
      setCancelDialogOpen(false); setSelectedBooking(null); fetchBookings(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Reschedule
  const handleReschedule = async () => {
    if (!selectedBooking || !newDate || !newSlot) return;
    try {
      await axios.put(`${API}/admin/bookings/${selectedBooking.id}`, { booking_date: newDate, time_slot: newSlot }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Réservation modifiée');
      setRescheduleDialogOpen(false); setSelectedBooking(null); setNewDate(''); setNewSlot(''); fetchBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Notify
  const handleNotify = async () => {
    if (!notifySubject || !notifyMessage) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/bookings/notify`, { booking_ids: notifyBookingIds, subject: notifySubject, message: notifyMessage }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);
      setNotifyDialogOpen(false); setNotifySubject(''); setNotifyMessage(''); setNotifyBookingIds([]);
    } catch (e) { toast.error('Erreur d\'envoi'); }
    setSending(false);
  };

  // Manual Booking
  const handleManualBooking = async () => {
    if (!manualForm.trip_id || !manualForm.booking_date || !manualForm.time_slot) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (manualForm.generate_payment_link && !manualForm.customer_email) {
      toast.error('L\'email client est obligatoire pour envoyer un lien de paiement');
      return;
    }
    const validParticipants = manualForm.participants.filter(p => p.first_name && p.last_name);
    if (validParticipants.length === 0) { toast.error('Ajoutez au moins un participant'); return; }
    try {
      const loadMsg = manualForm.generate_payment_link ? 'Création du lien de paiement...' : 'Ajout en cours...';
      const toastId = toast.loading(loadMsg);
      const res = await axios.post(`${API}/admin/bookings/manual`, {
        ...manualForm,
        participants: validParticipants.map(p => ({ ...p, age: parseInt(p.age) || 0 })),
        selected_duration: manualForm.selected_duration ? parseFloat(manualForm.selected_duration) : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.dismiss(toastId);
      
      if (manualForm.generate_payment_link && res.data.payment_link) {
        toast.success(`Lien de paiement envoyé à ${manualForm.customer_email}`);
      } else if (manualForm.generate_payment_link) {
        toast.success('Réservation ajoutée (erreur lors de la création du lien de paiement)');
      } else {
        toast.success('Réservation ajoutée');
      }
      setManualBookingDialogOpen(false);
      setManualForm({ trip_id: '', booking_date: '', time_slot: '', selected_duration: null, is_privatization: false, customer_email: '', customer_phone: '', notes: '', payment_status: 'on_site', send_to_client: false, generate_payment_link: false, participants: [{ first_name: '', last_name: '', age: '', phone: '' }] });
      fetchBookings(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Export
  const handleExport = async () => {
    try {
      const range = getDateRange();
      let url = `${API}/admin/bookings/export?`;
      if (range.date) url += `date=${range.date}`;
      else url += `start_date=${range.start}&end_date=${range.end}`;
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      link.download = `sorties_peche_${range.date || range.start}.csv`;
      link.click();
      toast.success('Export téléchargé');
    } catch (e) { toast.error('Erreur export'); }
  };

  // Blocked dates helpers
  const getBlocksForDate = (dateStr) => blockedDates.filter(b => b.date === dateStr);
  const isDateFullyBlocked = (dateStr) => blockedDates.some(b => b.date === dateStr && b.block_type === 'full_day');
  const isTripBlockedOnDate = (dateStr, tripId) => blockedDates.some(b => b.date === dateStr && ((b.block_type === 'full_day') || (b.block_type === 'trip_specific' && b.trip_id === tripId)));

  const handleBlockDate = async () => {
    if (!blockForm.date) { toast.error('Date requise'); return; }
    try {
      await axios.post(`${API}/admin/blocked-dates`, blockForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(blockForm.block_type === 'full_day' ? 'Journée bloquée' : 'Sortie bloquée pour ce jour');
      setBlockDialogOpen(false);
      setBlockForm({ date: '', block_type: 'full_day', trip_id: '', reason: '' });
      fetchBlockedDates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUnblock = async (blockId) => {
    try {
      await axios.delete(`${API}/admin/blocked-dates/${blockId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Déblocage effectué');
      fetchBlockedDates();
    } catch (e) { toast.error('Erreur'); }
  };

  const openBlockDialog = (date = null) => {
    const dateStr = date ? format(date, 'yyyy-MM-dd') : format(currentDate, 'yyyy-MM-dd');
    setBlockForm({ date: dateStr, block_type: 'full_day', trip_id: '', reason: '' });
    setBlockDialogOpen(true);
  };

  // Quick toggle: block or unblock a specific trip on a specific date
  const toggleTripBlock = async (dateStr, tripId) => {
    const existing = blockedDates.find(b => b.date === dateStr && b.block_type === 'trip_specific' && b.trip_id === tripId);
    if (existing) {
      await handleUnblock(existing.id);
    } else {
      try {
        await axios.post(`${API}/admin/blocked-dates`, { date: dateStr, block_type: 'trip_specific', trip_id: tripId, reason: '' }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Sortie mise en pause');
        fetchBlockedDates();
      } catch (e) { toast.error('Erreur'); }
    }
  };

  // Quick toggle: block or unblock full day
  const toggleFullDayBlock = async (dateStr) => {
    const existing = blockedDates.find(b => b.date === dateStr && b.block_type === 'full_day');
    if (existing) {
      await handleUnblock(existing.id);
    } else {
      try {
        await axios.post(`${API}/admin/blocked-dates`, { date: dateStr, block_type: 'full_day', trip_id: null, reason: '' }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Journée bloquée');
        fetchBlockedDates();
      } catch (e) { toast.error('Erreur'); }
    }
  };

  // Helpers
  const groupBookings = (arr) => {
    const grouped = {};
    arr.filter(b => b.booking_status !== 'cancelled').forEach(b => {
      const date = b.booking_date;
      const tripKey = b.trip_name_fr || 'N/A';
      const slotKey = b.time_slot;
      if (!grouped[date]) grouped[date] = {};
      if (!grouped[date][tripKey]) grouped[date][tripKey] = {};
      if (!grouped[date][tripKey][slotKey]) grouped[date][tripKey][slotKey] = [];
      grouped[date][tripKey][slotKey].push(b);
    });
    return grouped;
  };

  const getBookingsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings.filter(b => b.booking_date === dateStr && b.booking_status !== 'cancelled');
  };

  const getTitle = () => {
    if (viewMode === 'day') return format(currentDate, 'EEEE dd MMMM yyyy', { locale: fr });
    if (viewMode === 'week') {
      const s = startOfWeek(currentDate, { weekStartsOn: 1 });
      const e = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(s, 'dd MMM', { locale: fr })} - ${format(e, 'dd MMM yyyy', { locale: fr })}`;
    }
    return format(currentDate, 'MMMM yyyy', { locale: fr });
  };

  const getTotalPassengers = (arr) => arr.filter(b => b.booking_status !== 'cancelled').reduce((s, b) => s + (b.participants?.length || 0), 0);

  const getPaymentType = (booking) => {
    if (booking.payment_status === 'paid' && booking.payment_type !== 'on_site') return { label: 'Payé en ligne', color: 'bg-green-100 text-green-700 border-green-200' };
    if (booking.payment_status === 'paid') return { label: 'Payé', color: 'bg-green-100 text-green-700 border-green-200' };
    if (booking.payment_type === 'on_site' || booking.payment_status === 'on_site') return { label: 'À encaisser', color: 'bg-orange-100 text-orange-700 border-orange-200' };
    if (booking.payment_status === 'completed') return { label: 'Payé en ligne', color: 'bg-green-100 text-green-700 border-green-200' };
    return { label: 'À encaisser', color: 'bg-orange-100 text-orange-700 border-orange-200' };
  };

  // Open notify for all bookings of a slot
  const openNotifyForSlot = (slotBookings) => {
    setNotifyBookingIds(slotBookings.map(b => b.id));
    setNotifySubject('');
    setNotifyMessage('');
    setNotifyDialogOpen(true);
  };

  const openNotifyForBooking = (booking) => {
    setNotifyBookingIds([booking.id]);
    setNotifySubject('');
    setNotifyMessage('');
    setNotifyDialogOpen(true);
  };

  const navItems = [
    { path: '/admin/dashboard', icon: CalendarDots, label: 'Agenda' },
    { path: '/admin/invoices', icon: Receipt, label: 'Factures' },
    { path: '/admin/trips', icon: FishSimple, label: 'Sorties Pêche' },
    { path: '/admin/gallery', icon: Images, label: 'Galerie' },
    { path: '/admin/analytics', icon: ChartLine, label: 'Analytics' },
  ];
  const isCurrentPage = (path) => location.pathname === path;

  const selectedTrip = trips.find(t => t.id === manualForm.trip_id);

  if (loading && !bookings.length) {
    return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><div className="spinner" /></div>;
  }

  const grouped = groupBookings(bookings);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-[#000000] text-white z-30 print:hidden">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-10 w-10 object-contain rounded-full" />
            <span className="text-lg font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </Link>
        </div>
        <nav className="mt-6 px-3">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${isCurrentPage(item.path) ? 'bg-[#b7ede0] text-[#000000] font-semibold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
              <item.icon size={20} />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2">
          <Link to="/"><Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"><House size={20} className="mr-3" />Voir le site</Button></Link>
          <Button variant="ghost" onClick={() => { logout(); navigate('/admin'); }} className="w-full justify-start text-gray-400 hover:text-[#FF3B30] hover:bg-red-500/10"><SignOut size={20} className="mr-3" />Déconnexion</Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#000000] text-white px-4 py-3 print:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-8 w-8 object-contain rounded-full" />
            <span className="font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white">{mobileMenuOpen ? <X size={24} /> : <List size={24} />}</Button>
        </div>
        {mobileMenuOpen && (
          <nav className="mt-4 pb-4 border-t border-white/10 pt-4">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 ${isCurrentPage(item.path) ? 'bg-[#b7ede0] text-[#000000]' : 'text-gray-400'}`}>
                <item.icon size={20} /><span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-8" ref={printRef}>
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-6 print:hidden">
              {[
                { icon: CurrencyEur, label: 'Revenus', value: `${stats.total_revenue?.toFixed(0) || 0}€`, color: 'text-[#b7ede0]', bg: 'bg-[#b7ede0]/10' },
                { icon: CalendarDots, label: 'Réservations', value: stats.total_bookings || 0, color: 'text-[#10B981]', bg: 'bg-[#10B981]/10' },
                { icon: Users, label: 'Passagers', value: getTotalPassengers(bookings), color: 'text-[#8B5CF6]', bg: 'bg-[#8B5CF6]/10' },
                { icon: Prohibit, label: 'Annulations', value: `${stats.cancellation_rate || 0}%`, color: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]/10' }
              ].map((s, i) => (
                <Card key={i} className="stat-card"><CardContent className="p-3 sm:p-4"><div className="flex items-center gap-2 sm:gap-3">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}><s.icon size={18} className={s.color} /></div>
                  <div className="min-w-0"><p className="text-[10px] sm:text-xs text-[#4B5563] truncate">{s.label}</p><p className="text-sm sm:text-lg font-bold">{s.value}</p></div>
                </div></CardContent></Card>
              ))}
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-col gap-3 mb-6 print:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 sm:gap-2">
                <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={navigatePrev} data-testid="nav-prev"><CaretLeft size={16} /></Button>
                <Button variant="outline" size="sm" className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3" onClick={goToday}>Aujourd'hui</Button>
                <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={navigateNext} data-testid="nav-next"><CaretRight size={16} /></Button>
              </div>
              <div className="flex bg-[#F1F5F9] rounded-lg p-0.5 sm:p-1">
                {[{ k: 'day', l: 'J', fl: 'Jour', i: CalendarBlank }, { k: 'week', l: 'S', fl: 'Semaine', i: Rows }, { k: 'month', l: 'M', fl: 'Mois', i: SquaresFour }].map(v => (
                  <button key={v.k} onClick={() => setViewMode(v.k)} data-testid={`view-${v.k}`}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${viewMode === v.k ? 'bg-[#000000] text-[#b7ede0]' : 'text-[#4B5563] hover:text-[#000000]'}`}>
                    <v.i size={14} className="inline mr-0.5 sm:mr-1" /><span className="hidden sm:inline">{v.fl}</span><span className="sm:hidden">{v.l}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <h2 className="text-sm sm:text-lg font-semibold capitalize font-['Playfair_Display']" data-testid="agenda-title">
                {getTitle()}
              </h2>
              {viewMode === 'day' && isDateFullyBlocked(format(currentDate, 'yyyy-MM-dd')) && (
                <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px] sm:text-xs">Bloquée</Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {/* Block/Unblock button - only in week/month views */}
              {(viewMode === 'week' || viewMode === 'month') && (
                <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 gap-1 text-xs h-8" data-testid="block-day-btn"
                  onClick={() => openBlockDialog()}>
                  <Prohibit size={14} />Bloquer
                </Button>
              )}
              <Button size="sm" onClick={() => setManualBookingDialogOpen(true)} className="bg-[#b7ede0] hover:bg-[#9ee5d4] text-[#000000] gap-1 text-xs h-8" data-testid="add-manual-booking">
                <UserPlus size={14} />Ajouter
              </Button>
              <Button variant="outline" size="sm" className="gap-1 text-xs h-8" onClick={() => window.print()}><Printer size={14} /></Button>
              <Button variant="outline" size="sm" className="gap-1 text-xs h-8" onClick={handleExport} data-testid="export-btn"><Download size={14} />CSV</Button>
            </div>
          </div>

          {/* Print Header */}
          <div className="hidden print:block mb-6">
            <h1 className="text-2xl font-bold">CALVI FISHING - Liste des passagers</h1>
            <p className="text-lg capitalize">{getTitle()}</p>
          </div>

          {/* DAY VIEW */}
          {viewMode === 'day' && (() => {
            const dateStr = format(currentDate, 'yyyy-MM-dd');
            const dayFullyBlocked = isDateFullyBlocked(dateStr);
            const activeTrips = trips.filter(t => t.is_active);

            return (
            <div className="space-y-4">
              {/* Full day toggle */}
              <Card className={`overflow-hidden transition-all ${dayFullyBlocked ? 'border-red-200 bg-red-50/30' : 'border-[#E2E8F0]'}`}>
                <CardContent className="py-4 px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Prohibit size={20} className={dayFullyBlocked ? 'text-red-500' : 'text-[#CBD5E1]'} />
                      <span className="font-semibold text-sm">Bloquer toute la journée</span>
                      {dayFullyBlocked && <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Toutes les sorties en pause</Badge>}
                    </div>
                    <Switch
                      checked={dayFullyBlocked}
                      onCheckedChange={() => toggleFullDayBlock(dateStr)}
                      data-testid="toggle-full-day"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Each trip card */}
              {activeTrips.map(trip => {
                const tripBlocked = isTripBlockedOnDate(dateStr, trip.id);
                const tripBookings = bookings.filter(b => b.trip_id === trip.id && b.booking_date === dateStr && b.booking_status !== 'cancelled');
                const totalPax = tripBookings.reduce((s, b) => s + (b.participants?.length || 0), 0);
                const bookingsBySlot = {};
                tripBookings.forEach(b => {
                  if (!bookingsBySlot[b.time_slot]) bookingsBySlot[b.time_slot] = [];
                  bookingsBySlot[b.time_slot].push(b);
                });

                return (
                  <Card key={trip.id} className={`overflow-hidden transition-all ${tripBlocked ? 'opacity-60' : ''}`} data-testid={`trip-card-${trip.id}`}>
                    <CardHeader className={`py-3 px-6 ${tripBlocked ? 'bg-[#4B5563]' : 'bg-[#000000]'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FishSimple size={22} className="text-[#b7ede0]" />
                          <CardTitle className="text-base font-['Playfair_Display'] text-white">{trip.name_fr}</CardTitle>
                          {trip.duration_options && trip.duration_options.length > 0 && (
                            <span className="text-xs text-[#b7ede0]">({trip.duration_options.map(o => o.label).join(' / ')})</span>
                          )}
                          {tripBlocked && <Badge className="bg-red-500/20 text-red-300 border-red-400/30 text-xs">En pause</Badge>}
                        </div>
                        <div className="flex items-center gap-4">
                          {totalPax > 0 && <Badge className="bg-[#b7ede0] text-[#000000] text-xs">{totalPax} passager{totalPax > 1 ? 's' : ''}</Badge>}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white/60">{tripBlocked ? 'En pause' : 'Active'}</span>
                            <Switch
                              checked={!tripBlocked}
                              onCheckedChange={() => !dayFullyBlocked && toggleTripBlock(dateStr, trip.id)}
                              disabled={dayFullyBlocked}
                              data-testid={`toggle-trip-${trip.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {/* Slots with bookings */}
                      {Object.keys(bookingsBySlot).length > 0 ? (
                        Object.entries(bookingsBySlot).map(([slotName, slotBookings]) => (
                          <div key={slotName} className="border-b last:border-0">
                            <div className="flex items-center gap-2 px-6 py-3 bg-[#F8FAFC]">
                              <Clock size={16} className="text-[#b7ede0]" />
                              <span className="font-semibold text-sm">{slotName}</span>
                              <Badge variant="secondary" className="ml-auto text-xs">
                                {slotBookings.reduce((s, b) => s + (b.participants?.length || 0), 0)} pers.
                              </Badge>
                              <Button variant="ghost" size="sm" onClick={() => openNotifyForSlot(slotBookings)} className="gap-1 text-xs print:hidden" data-testid="notify-slot">
                                <EnvelopeSimple size={14} />Informer
                              </Button>
                            </div>
                            <div className="overflow-x-auto">
                            <Table className="min-w-[700px]">
                              <TableHeader><TableRow>
                                <TableHead className="pl-6">Prénom</TableHead>
                                <TableHead>Nom</TableHead>
                                <TableHead>Âge</TableHead>
                                <TableHead>Téléphone</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Paiement</TableHead>
                                <TableHead>Montant</TableHead>
                                <TableHead className="print:hidden">Actions</TableHead>
                              </TableRow></TableHeader>
                              <TableBody>
                                {slotBookings.map(booking => booking.participants.map((p, pi) => {
                                  const pType = getPaymentType(booking);
                                  return (
                                    <TableRow key={`${booking.id}-${pi}`} data-testid={`passenger-row-${booking.id}-${pi}`}>
                                      <TableCell className="pl-6 font-medium">{p.first_name}</TableCell>
                                      <TableCell className="font-medium">{p.last_name}</TableCell>
                                      <TableCell>{p.age} ans</TableCell>
                                      <TableCell>{p.phone}</TableCell>
                                      <TableCell className="text-xs">{booking.customer_email}</TableCell>
                                      <TableCell>{pi === 0 && <span className={`px-2 py-1 rounded-full text-xs font-medium border ${pType.color}`}>{pType.label}</span>}</TableCell>
                                      <TableCell>{pi === 0 && <span className="font-bold">{booking.total_amount}€</span>}</TableCell>
                                      <TableCell className="print:hidden">{pi === 0 && (
                                        <div className="flex gap-0.5">
                                          <Button variant="ghost" size="sm" title="Voir" onClick={() => { setSelectedBooking(booking); setDetailDialogOpen(true); }}><Eye size={14} /></Button>
                                          <Button variant="ghost" size="sm" title="Modifier" onClick={() => { setSelectedBooking(booking); setNewDate(booking.booking_date); setRescheduleDialogOpen(true); }}><Pencil size={14} /></Button>
                                          <Button variant="ghost" size="sm" title="Informer" onClick={() => openNotifyForBooking(booking)}><EnvelopeSimple size={14} /></Button>
                                          <Button variant="ghost" size="sm" title="Annuler" className="text-[#FF3B30]" onClick={() => { setSelectedBooking(booking); setCancelDialogOpen(true); }}><Trash size={14} /></Button>
                                        </div>
                                      )}</TableCell>
                                    </TableRow>
                                  );
                                }))}
                              </TableBody>
                            </Table>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-6 py-4 text-sm text-[#4B5563] flex items-center gap-2">
                          <Clock size={14} className="text-[#CBD5E1]" />
                          {trip.time_slots?.map(s => s.label).join(', ')} — Aucune réservation
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            );
          })()}

          {/* WEEK VIEW */}
          {viewMode === 'week' && (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {eachDayOfInterval({ start: startOfWeek(currentDate, { weekStartsOn: 1 }), end: endOfWeek(currentDate, { weekStartsOn: 1 }) }).map(day => {
                const dayBookings = getBookingsForDay(day);
                const today = isToday(day);
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayBlocked = isDateFullyBlocked(dateStr);
                const dayBlocks = getBlocksForDate(dateStr);
                return (
                  <Card key={day.toISOString()} className={`cursor-pointer transition-all hover:shadow-md ${today ? 'ring-2 ring-[#b7ede0]' : ''} ${dayBlocked ? 'ring-2 ring-red-300 bg-red-50/30' : ''}`}
                    onClick={() => { setCurrentDate(day); setViewMode('day'); }} data-testid={`week-day-${dateStr}`}>
                    <CardContent className="p-3 min-h-[160px]">
                      <div className={`text-center mb-2 pb-2 border-b ${today ? 'border-[#b7ede0]' : dayBlocked ? 'border-red-200' : 'border-[#E2E8F0]'}`}>
                        <div className="text-xs text-[#4B5563] uppercase">{format(day, 'EEE', { locale: fr })}</div>
                        <div className={`text-xl font-bold ${today ? 'text-[#b7ede0]' : dayBlocked ? 'text-red-400' : ''}`}>{format(day, 'd')}</div>
                      </div>
                      {dayBlocked && (
                        <div className="text-[10px] bg-red-100 text-red-600 rounded px-2 py-1 mb-1.5 text-center font-medium flex items-center justify-center gap-1">
                          <Prohibit size={10} />Bloqué
                        </div>
                      )}
                      {!dayBlocked && dayBlocks.length > 0 && (
                        <div className="text-[10px] bg-orange-100 text-orange-600 rounded px-2 py-1 mb-1.5 text-center font-medium">
                          {dayBlocks.length} blocage{dayBlocks.length > 1 ? 's' : ''}
                        </div>
                      )}
                      {dayBookings.length === 0 && !dayBlocked ? <p className="text-xs text-[#CBD5E1] text-center mt-4">-</p> : (
                        <div className="space-y-1.5">
                          {Object.entries(dayBookings.reduce((acc, b) => { const k = b.trip_name_fr || 'N/A'; if (!acc[k]) acc[k] = []; acc[k].push(b); return acc; }, {})).map(([tn, tb]) => (
                            <div key={tn} className="bg-[#b7ede0]/10 rounded-md px-2 py-1.5">
                              <div className="text-xs font-semibold truncate">{tn}</div>
                              <div className="flex items-center gap-1 text-[10px] text-[#4B5563]"><Users size={10} />{tb.reduce((s, b) => s + (b.participants?.length || 0), 0)} pers.</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* MONTH VIEW */}
          {viewMode === 'month' && (
            <Card><CardContent className="p-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                  <div key={d} className="text-center text-xs font-bold text-[#4B5563] uppercase py-2">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {eachDayOfInterval({ start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 }) }).map(day => {
                  const db = getBookingsForDay(day);
                  const inMonth = isSameMonth(day, currentDate);
                  const today = isToday(day);
                  const totalPax = db.reduce((s, b) => s + (b.participants?.length || 0), 0);
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const dayBlocked = isDateFullyBlocked(dateStr);
                  const dayBlocks = getBlocksForDate(dateStr);
                  return (
                    <div key={day.toISOString()} onClick={() => { setCurrentDate(day); setViewMode('day'); }}
                      className={`min-h-[80px] p-1.5 rounded-lg cursor-pointer transition-all border ${!inMonth ? 'opacity-30 border-transparent' : 'border-[#E2E8F0] hover:border-[#b7ede0]'} ${today ? 'bg-[#b7ede0]/10 border-[#b7ede0]' : ''} ${dayBlocked && inMonth ? 'bg-red-50 border-red-200 hover:border-red-300' : ''}`}
                      data-testid={`month-day-${dateStr}`}>
                      <div className={`text-sm font-medium mb-1 ${today ? 'text-[#b7ede0]' : ''} ${dayBlocked ? 'text-red-400' : ''}`}>{format(day, 'd')}</div>
                      {dayBlocked && inMonth && (
                        <div className="text-[9px] bg-red-100 text-red-600 rounded px-1 py-0.5 truncate flex items-center gap-0.5">
                          <Prohibit size={8} />Bloqué
                        </div>
                      )}
                      {!dayBlocked && dayBlocks.length > 0 && inMonth && (
                        <div className="text-[9px] bg-orange-100 text-orange-600 rounded px-1 py-0.5 truncate">{dayBlocks.length} blocage</div>
                      )}
                      {db.length > 0 && <div className="space-y-0.5 mt-0.5">
                        <div className="text-[10px] bg-[#000000] text-[#b7ede0] rounded px-1 py-0.5 truncate">{db.length} sortie{db.length > 1 ? 's' : ''}</div>
                        <div className="text-[10px] text-[#4B5563]">{totalPax} pers.</div>
                      </div>}
                    </div>
                  );
                })}
              </div>
            </CardContent></Card>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-['Playfair_Display']">Détails de la réservation</DialogTitle></DialogHeader>
          {selectedBooking && (<div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-[#4B5563]">Sortie :</span> <span className="font-medium">{selectedBooking.trip_name_fr}</span></div>
              <div><span className="text-[#4B5563]">Date :</span> <span className="font-medium">{selectedBooking.booking_date}</span></div>
              <div><span className="text-[#4B5563]">Créneau :</span> <span className="font-medium">{selectedBooking.time_slot}</span></div>
              <div><span className="text-[#4B5563]">Montant :</span> <span className="font-bold">{selectedBooking.total_amount}€</span></div>
              <div><span className="text-[#4B5563]">Email :</span> <span className="font-medium">{selectedBooking.customer_email}</span></div>
              <div><span className="text-[#4B5563]">Tél :</span> <span className="font-medium">{selectedBooking.customer_phone}</span></div>
              <div><span className="text-[#4B5563]">Paiement :</span> <span className={`px-2 py-0.5 rounded-full text-xs ${getPaymentType(selectedBooking).color}`}>{getPaymentType(selectedBooking).label}</span></div>
              <div><span className="text-[#4B5563]">Statut :</span> <Badge variant={selectedBooking.booking_status === 'confirmed' ? 'default' : 'destructive'}>{selectedBooking.booking_status}</Badge></div>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Passagers ({selectedBooking.participants?.length})</h4>
              <div className="space-y-2">
                {selectedBooking.participants?.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 bg-[#F8FAFC] p-3 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-[#b7ede0] flex items-center justify-center text-sm font-bold">{i + 1}</div>
                    <div><p className="font-medium text-sm">{p.first_name} {p.last_name}</p><p className="text-xs text-[#4B5563]">{p.age} ans - {p.phone}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>)}
          <DialogFooter><Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Annuler la réservation</DialogTitle><DialogDescription>Êtes-vous sûr ? Le client sera informé de l'annulation.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Non, garder</Button><Button variant="destructive" onClick={handleCancelBooking}>Oui, annuler</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Playfair_Display']">Modifier la réservation</DialogTitle><DialogDescription>Changez la date et/ou le créneau horaire.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label className="font-medium">Nouvelle date</Label><Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="mt-1" /></div>
            <div><Label className="font-medium">Nouveau créneau</Label>
              <Select value={newSlot} onValueChange={setNewSlot}><SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{trips.flatMap(t => t.time_slots || []).filter((s, i, a) => a.findIndex(x => x.label === s.label) === i).map(s => (
                  <SelectItem key={s.label} value={s.label}>{s.label}</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>Annuler</Button><Button onClick={handleReschedule} disabled={!newDate || !newSlot}>Confirmer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify Dialog */}
      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-['Playfair_Display'] flex items-center gap-2"><EnvelopeSimple size={22} className="text-[#b7ede0]" />Informer les participants</DialogTitle>
            <DialogDescription>{notifyBookingIds.length} réservation(s) sélectionnée(s)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-2">
              {['Report de créneau', 'Annulation météo', 'Changement d\'horaire', 'Remerciements', 'Information importante'].map(t => (
                <button key={t} onClick={() => setNotifySubject(t)} className={`px-3 py-1.5 rounded-full text-xs border transition-all ${notifySubject === t ? 'bg-[#000000] text-[#b7ede0] border-[#000000]' : 'border-[#E2E8F0] hover:border-[#b7ede0]'}`}>{t}</button>
              ))}
            </div>
            <div><Label className="font-medium">Objet</Label><Input value={notifySubject} onChange={e => setNotifySubject(e.target.value)} placeholder="Objet du message" className="mt-1" /></div>
            <div><Label className="font-medium">Message</Label>
              <textarea value={notifyMessage} onChange={e => setNotifyMessage(e.target.value)} rows={5} placeholder="Votre message aux participants..." className="w-full mt-1 rounded-lg border border-[#E2E8F0] p-3 text-sm resize-none focus:outline-none focus:border-[#b7ede0] focus:ring-1 focus:ring-[#b7ede0]" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNotifyDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleNotify} disabled={!notifySubject || !notifyMessage || sending} className="bg-[#000000] text-[#b7ede0] gap-1">
              {sending ? 'Envoi...' : <><EnvelopeSimple size={16} />Envoyer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Booking Dialog */}
      <Dialog open={manualBookingDialogOpen} onOpenChange={setManualBookingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Playfair_Display'] flex items-center gap-2"><UserPlus size={22} className="text-[#b7ede0]" />Ajouter une réservation manuelle</DialogTitle>
            <DialogDescription>Ajoutez une réservation manuellement</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-medium">Sortie pêche *</Label>
                <Select value={manualForm.trip_id} onValueChange={v => setManualForm({ ...manualForm, trip_id: v, time_slot: '', selected_duration: null })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir une sortie" /></SelectTrigger>
                  <SelectContent>{trips.filter(t => t.is_active).map(t => <SelectItem key={t.id} value={t.id}>{t.name_fr}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-medium">Date *</Label>
                <Input type="date" value={manualForm.booking_date} onChange={e => setManualForm({ ...manualForm, booking_date: e.target.value })} className="mt-1" />
              </div>
            </div>
            {selectedTrip?.duration_options && (
              <div>
                <Label className="font-medium">Durée</Label>
                <Select value={manualForm.selected_duration?.toString() || ''} onValueChange={v => setManualForm({ ...manualForm, selected_duration: v, time_slot: '' })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{selectedTrip.duration_options.map(o => <SelectItem key={o.label} value={o.duration_hours.toString()}>{o.label} - {o.price_per_person}€/pers.</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {selectedTrip && (
              <div>
                <Label className="font-medium">Créneau *</Label>
                <Select value={manualForm.time_slot} onValueChange={v => setManualForm({ ...manualForm, time_slot: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir un créneau" /></SelectTrigger>
                  <SelectContent>
                    {selectedTrip.time_slots.filter(s => {
                      if (!selectedTrip.duration_options || !manualForm.selected_duration) return true;
                      const opt = selectedTrip.duration_options.find(o => o.duration_hours.toString() === manualForm.selected_duration);
                      return opt ? s.label.includes(`(${opt.label})`) : true;
                    }).map(s => <SelectItem key={s.label} value={s.label}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-medium">Email client</Label><Input value={manualForm.customer_email} onChange={e => setManualForm({ ...manualForm, customer_email: e.target.value })} className="mt-1" placeholder="email@client.com" /></div>
              <div><Label className="font-medium">Téléphone client</Label><Input value={manualForm.customer_phone} onChange={e => setManualForm({ ...manualForm, customer_phone: e.target.value })} className="mt-1" placeholder="06 ..." /></div>
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={manualForm.is_privatization} onChange={e => setManualForm({ ...manualForm, is_privatization: e.target.checked })} className="w-4 h-4 rounded" />
              <Label>Privatisation</Label>
            </div>

            {/* Payment status */}
            <div>
              <Label className="font-medium">Mode de paiement *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setManualForm({ ...manualForm, payment_status: 'paid', generate_payment_link: false })}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border-2 transition-all font-medium text-xs ${manualForm.payment_status === 'paid' && !manualForm.generate_payment_link ? 'border-[#10B981] bg-[#ECFDF5] text-[#065F46]' : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#10B981]/50'}`}
                  data-testid="payment-status-paid"
                >
                  <CurrencyEur size={20} />
                  Payé
                </button>
                <button
                  type="button"
                  onClick={() => setManualForm({ ...manualForm, payment_status: 'on_site', generate_payment_link: false })}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border-2 transition-all font-medium text-xs ${manualForm.payment_status === 'on_site' && !manualForm.generate_payment_link ? 'border-[#F59E0B] bg-[#FFF7ED] text-[#9A3412]' : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#F59E0B]/50'}`}
                  data-testid="payment-status-on-site"
                >
                  <Clock size={20} />
                  Sur place
                </button>
                <button
                  type="button"
                  onClick={() => setManualForm({ ...manualForm, payment_status: 'pending', generate_payment_link: true })}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border-2 transition-all font-medium text-xs ${manualForm.generate_payment_link ? 'border-[#3B82F6] bg-[#EFF6FF] text-[#1E40AF]' : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#3B82F6]/50'}`}
                  data-testid="payment-status-link"
                >
                  <EnvelopeSimple size={20} />
                  Lien de paiement
                </button>
              </div>
              {manualForm.generate_payment_link && (
                <p className="text-xs text-[#3B82F6] mt-2 bg-[#EFF6FF] px-3 py-2 rounded-lg">
                  Un lien de paiement Square sera généré et envoyé par email au client. L'email est obligatoire.
                </p>
              )}
            </div>
            
            {/* Send to client checkbox (only when not payment link) */}
            {!manualForm.generate_payment_link && (
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={manualForm.send_to_client} onChange={e => setManualForm({ ...manualForm, send_to_client: e.target.checked })} className="w-4 h-4 rounded accent-[#b7ede0]" data-testid="send-to-client-checkbox" />
                <Label className="text-sm">Envoyer la confirmation par email au client</Label>
              </div>
            )}
            
            {/* Participants */}
            <div>
              <Label className="font-medium text-base">Passagers</Label>
              <div className="space-y-3 mt-2">
                {manualForm.participants.map((p, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 items-end">
                    <div><Label className="text-xs">Prénom *</Label><Input value={p.first_name} onChange={e => { const np = [...manualForm.participants]; np[i].first_name = e.target.value; setManualForm({ ...manualForm, participants: np }); }} /></div>
                    <div><Label className="text-xs">Nom *</Label><Input value={p.last_name} onChange={e => { const np = [...manualForm.participants]; np[i].last_name = e.target.value; setManualForm({ ...manualForm, participants: np }); }} /></div>
                    <div><Label className="text-xs">Âge</Label><Input type="number" value={p.age} onChange={e => { const np = [...manualForm.participants]; np[i].age = e.target.value; setManualForm({ ...manualForm, participants: np }); }} /></div>
                    <div><Label className="text-xs">Téléphone</Label><Input value={p.phone} onChange={e => { const np = [...manualForm.participants]; np[i].phone = e.target.value; setManualForm({ ...manualForm, participants: np }); }} /></div>
                    <div>{manualForm.participants.length > 1 && <Button variant="ghost" size="sm" className="text-[#FF3B30]" onClick={() => { const np = manualForm.participants.filter((_, j) => j !== i); setManualForm({ ...manualForm, participants: np }); }}><Trash size={14} /></Button>}</div>
                  </div>
                ))}
              </div>
              {manualForm.participants.length < 4 && (
                <Button variant="outline" size="sm" className="mt-3 gap-1 border-dashed border-[#b7ede0]" onClick={() => setManualForm({ ...manualForm, participants: [...manualForm.participants, { first_name: '', last_name: '', age: '', phone: '' }] })}>
                  <PlusCircle size={14} />Ajouter un passager
                </Button>
              )}
            </div>
            <div><Label className="font-medium">Notes</Label><Input value={manualForm.notes} onChange={e => setManualForm({ ...manualForm, notes: e.target.value })} className="mt-1" placeholder="Notes internes..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualBookingDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleManualBooking} className="bg-[#b7ede0] text-[#000000] gap-1"><UserPlus size={16} />Ajouter la réservation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Date Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
              <Prohibit size={22} className="text-red-500" />Bloquer une date
            </DialogTitle>
            <DialogDescription>Empêcher les réservations pour cette date</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-medium">Date</Label>
              <Input type="date" value={blockForm.date} onChange={e => setBlockForm({ ...blockForm, date: e.target.value })} className="mt-1" data-testid="block-date-input" />
            </div>
            <div>
              <Label className="font-medium">Type de blocage</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button type="button" onClick={() => setBlockForm({ ...blockForm, block_type: 'full_day', trip_id: '' })}
                  className={`flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 transition-all ${blockForm.block_type === 'full_day' ? 'border-red-400 bg-red-50 text-red-700' : 'border-[#E5E7EB] text-[#6B7280] hover:border-red-200'}`}
                  data-testid="block-type-full">
                  <Prohibit size={24} />
                  <span className="text-xs font-semibold">Journée complète</span>
                  <span className="text-[10px]">Toutes les sorties</span>
                </button>
                <button type="button" onClick={() => setBlockForm({ ...blockForm, block_type: 'trip_specific' })}
                  className={`flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 transition-all ${blockForm.block_type === 'trip_specific' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-[#E5E7EB] text-[#6B7280] hover:border-orange-200'}`}
                  data-testid="block-type-trip">
                  <FishSimple size={24} />
                  <span className="text-xs font-semibold">Une sortie</span>
                  <span className="text-[10px]">Sortie spécifique</span>
                </button>
              </div>
            </div>
            {blockForm.block_type === 'trip_specific' && (
              <div>
                <Label className="font-medium">Sortie à bloquer</Label>
                <Select value={blockForm.trip_id} onValueChange={v => setBlockForm({ ...blockForm, trip_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="block-trip-select"><SelectValue placeholder="Choisir une sortie" /></SelectTrigger>
                  <SelectContent>{trips.filter(t => t.is_active).map(t => <SelectItem key={t.id} value={t.id}>{t.name_fr}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="font-medium">Raison (optionnel)</Label>
              <Input value={blockForm.reason} onChange={e => setBlockForm({ ...blockForm, reason: e.target.value })} className="mt-1" placeholder="Ex: Météo, maintenance..." data-testid="block-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleBlockDate} className="bg-red-500 hover:bg-red-600 text-white gap-1" data-testid="confirm-block-btn">
              <Prohibit size={16} />Bloquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
